import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { Configuration } from '../../config/configuration';
import { SystemDialogService } from '../services/system-dialog.service';
import { ToastService } from '../services/toast.service';

@Injectable({
  providedIn: 'root',
})
export class ErrorService {
  private disconnected$ = new Subject<boolean>();
  private timeout$ = new Subject<boolean>();

  constructor(
    private zone: NgZone,
    private router: Router,
    private configuration: Configuration,
    private translateService: TranslateService,
    private toastService: ToastService,
    private systemDialogService: SystemDialogService
  ) {
    this.timeout$.subscribe((result) => {
      if (result) {
        this.handleTimeoutError();
      }
    });

    this.disconnected$.subscribe((result) => {
      if (result) {
        this.handleDisconnectedError();
      }
    });
  }

  /**
   * @description 處理 status code 為 401 的情況
   */
  handleUnAuthorizedError(): void {
    this.zone.run(() => {
      this.router.navigate([this.configuration.routePath.noPermission]);
    });
  }

  /**
   * @description 處理 status code 為 400 的情況
   * @param errorCode 錯誤代碼
   * @param errorBody 錯誤資訊
   * @returns 回傳錯誤資訊，若已執行顯示系統提示則回傳 undefined
   */
  handleBusinessError(errorCode: string, errorBody: any): any | undefined {
    const alertTitle = this.translateService.instant('SigvLibrary.Error');
    switch (errorCode) {
      case 'TRNS.UserNotExist':
      case 'FORMAT.CustomError':
        break;
      default:
        // 使用 errorCode 透過 translate service 取得翻譯後文字，如文字未翻譯由錯誤資訊取得要顯示的錯誤訊息
        const mappingErrorMessage = this.translateService.instant(errorCode);
        if (mappingErrorMessage === errorCode) {
          // 由於不確定錯誤代碼與回傳資料格式的對應，直接依據錯誤資訊欄位(detail 與 errors)是否有值判別，取得顯示錯誤訊息
          if (errorBody.detail) {
            const description = errorBody.detail as string;
            this.systemDialogService.showErrorDialog(
              [{ description }],
              alertTitle
            );
            return;
          } else if (errorBody.errors) {
            const msgs = Object.values<string[]>(errorBody.errors)
              .reduce((sum, item) => sum.concat(item), [])
              .filter((x, idx, array) => array.indexOf(x) === idx)
              .map((msg) => {
                return {
                  description: msg,
                };
              });
            this.systemDialogService.showErrorDialog(msgs, alertTitle);
            return;
          }
        } else {
          this.systemDialogService.showErrorDialog(
            [{ description: mappingErrorMessage }],
            alertTitle
          );
          return;
        }
        break;
    }

    // switch case 未 return 把錯誤資訊回傳
    return Object.assign({}, errorBody, { errorCode });
  }

  /**
   * @description 處理 unknow error，顯示 toast 訊息
   */
  handleUnknowError(clientTraceId: string): void {
    this.translateService.get('SigvLibrary.UnknowError').subscribe({
      next: (msg) => {
        this.zone.run(() => {
          if (this.configuration.isErrorToastTrace) {
            const detailMsg = `Client-TraceId: ${clientTraceId}`;
            this.toastService.addSingle('error', msg, detailMsg, true);
          } else {
            this.toastService.error(msg);
          }
        });
      },
    });
  }

  /**
   * @description 顯示沒有網路連線錯誤，若已顯示網路連線錯誤視窗將不會重覆出現相同訊息，除非網路連線錯誤視窗被關閉
   */
  showDisconnectedError(): void {
    this.disconnected$.next(true);
  }

  /**
   * @description 顯示 TIMEOUT 錯誤，若已顯示 TIMEOUT 錯誤視窗將不會重覆出現相同訊息，除非 TIMEOUT 錯誤視窗被關閉
   */
  showTimeoutError(): void {
    this.timeout$.next(true);
  }

  private handleDisconnectedError(): void {
    const alertTitle = this.translateService.instant('SigvLibrary.Error');
    const description = this.translateService.instant(
      'SigvLibrary.Disconnected'
    );
    this.systemDialogService.showErrorDialog(
      [
        {
          description,
        },
      ],
      alertTitle,
      () => {
        this.disconnected$.next(false);
      }
    );
  }

  private handleTimeoutError(): void {
    const alertTitle = this.translateService.instant('SigvLibrary.Error');
    const description = this.translateService.instant('SigvLibrary.Timeout');
    this.systemDialogService.showErrorDialog(
      [
        {
          description,
        },
      ],
      alertTitle,
      () => {
        this.timeout$.next(false);
      }
    );
  }
}
